import * as React from 'react';
import { useEffect, useState } from 'react';
import { MIGRATION_BASE_URL } from './Services/migrationTarget.Service';
import { buildMigrationUrl, clearMigrationSession } from './Services/migrationLogin.Service';

/** Po tylu sekundach nastepuje automatyczne przeniesienie, jesli uzytkownik nie kliknie linku. */
export const MIGRATION_REDIRECT_SECONDS = 10;

/** Promien i obwod pierscienia licznika (SVG). */
const RING_RADIUS = 32;
const RING_CIRC = 2 * Math.PI * RING_RADIUS;

type Props = {
  /** Tryb podgladu (np. /migration-preview): licznik zapetla sie, brak przekierowania i czyszczenia sesji. */
  preview?: boolean;
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 999999,
  backgroundColor: 'rgba(15, 23, 42, 0.92)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  fontFamily: 'system-ui, -apple-system, sans-serif',
  overflow: 'auto'
};

const dialogStyle: React.CSSProperties = {
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.35)',
  maxWidth: '560px',
  width: '100%',
  padding: '40px 32px',
  textAlign: 'center',
  boxSizing: 'border-box'
};

const logoStyle: React.CSSProperties = {
  height: '44px',
  marginBottom: '28px'
};

const transitionRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  margin: '0 0 24px'
};

const pillOldStyle: React.CSSProperties = {
  padding: '6px 14px',
  borderRadius: '999px',
  background: '#f1f5f9',
  color: '#64748b',
  fontSize: '14px',
  fontWeight: 600,
  border: '1px solid #e2e8f0',
  whiteSpace: 'nowrap'
};

const pillNewStyle: React.CSSProperties = {
  padding: '6px 14px',
  borderRadius: '999px',
  background: '#4668fd',
  color: '#ffffff',
  fontSize: '14px',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  boxShadow: '0 6px 16px rgba(70, 104, 253, 0.35)'
};

const leadStyle: React.CSSProperties = {
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: 1.6,
  color: '#1f2937',
  margin: '0 0 14px'
};

const contentStyle: React.CSSProperties = {
  fontSize: '15px',
  lineHeight: 1.6,
  color: '#4b5563',
  margin: '0 0 24px'
};

const countdownWrapStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px'
};

const ringWrapStyle: React.CSSProperties = {
  position: 'relative',
  width: '80px',
  height: '80px'
};

const ringNumberStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '24px',
  fontWeight: 700,
  color: '#1f2937'
};

const countdownCaptionStyle: React.CSSProperties = {
  fontSize: '13px',
  color: '#6b7280'
};

const linkStyle: React.CSSProperties = {
  display: 'inline-block',
  marginTop: '24px',
  backgroundColor: '#4668fd',
  color: '#ffffff',
  textDecoration: 'none',
  fontSize: '16px',
  fontWeight: 600,
  padding: '14px 32px',
  borderRadius: '6px'
};

const urlStyle: React.CSSProperties = {
  display: 'block',
  marginTop: '12px',
  fontSize: '13px',
  color: '#9ca3af',
  wordBreak: 'break-all'
};

/**
 * Pelnoekranowy, niezamykalny modal informujacy o wygaszaniu panelu SellTracking 1.0.
 * Renderowany zamiast calej aplikacji - jedynym wyjatkiem jest ekran logowania.
 * Po MIGRATION_REDIRECT_SECONDS sekundach przenosi uzytkownika sam, jesli nie kliknie linku.
 * Tryb `preview` (np. /migration-preview) tylko podglada ekran: licznik zapetla sie, bez przekierowania.
 */
const MigrationModal: React.FC<Props> = ({ preview = false }) => {
  const targetUrl = buildMigrationUrl();
  const [secondsLeft, setSecondsLeft] = useState(MIGRATION_REDIRECT_SECONDS);

  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsLeft((previous) => {
        if (previous > 0) {
          return previous - 1;
        }
        // w podgladzie zapetlamy animacje; w realu zostajemy na 0 (chwile przed replace)
        return preview ? MIGRATION_REDIRECT_SECONDS : 0;
      });
    }, 1000);

    const timeout = preview
      ? undefined
      : setTimeout(() => {
          // token 2.0 jest juz w targetUrl (#token=); sprzatamy sesje 1.0, bo user wchodzi do 2.0
          clearMigrationSession();
          // replace, a nie assign - zeby przycisk "wstecz" nie wracal do wygaszonego panelu
          window.location.replace(targetUrl);
        }, MIGRATION_REDIRECT_SECONDS * 1000);

    return () => {
      clearInterval(interval);
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [targetUrl, preview]);

  const progress = secondsLeft / MIGRATION_REDIRECT_SECONDS;
  const dashOffset = RING_CIRC * (1 - progress);
  // po zresetowaniu (powrot do pelna) bez animacji, zeby pierscien nie "napelnial sie" wstecz
  const ringTransition = secondsLeft === MIGRATION_REDIRECT_SECONDS ? 'none' : 'stroke-dashoffset 1s linear';

  const onContinue = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (preview) {
      event.preventDefault();
      return;
    }
    clearMigrationSession();
  };

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true">
      <div style={dialogStyle}>
        <img src={'/images/icons/selltracking-vehis-logo.svg'} alt="SellTracking" style={logoStyle} />

        <div style={transitionRowStyle} aria-hidden="true">
          <span style={pillOldStyle}>SellTracking 1.0</span>
          <svg width="40" height="18" viewBox="0 0 40 18" fill="none">
            <path d="M2 9 H33" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" />
            <path
              d="M27 3 L35 9 L27 15"
              stroke="#94a3b8"
              strokeWidth="2.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span style={pillNewStyle}>SellTracking 2.0</span>
        </div>

        <p style={leadStyle}>
          Oddajemy do Państwa dyspozycji nową wersję serwisu, który został zaprojektowany z myślą
          o wygodzie użytkowników. Jest szybciej, prościej i bardziej komfortowo niż dotychczas.
        </p>

        <p style={contentStyle}>
          W ciągu {MIGRATION_REDIRECT_SECONDS} sek zostaną Państwo automatycznie przekierowani
          z panelu SellTracking 1.0 na panel SellTracking 2.0.
        </p>

        <div style={countdownWrapStyle}>
          <div style={ringWrapStyle}>
            <svg width="80" height="80" viewBox="0 0 80 80" style={{ transform: 'rotate(-90deg)' }}>
              <circle cx="40" cy="40" r={RING_RADIUS} fill="none" stroke="#e5e7eb" strokeWidth="6" />
              <circle
                cx="40"
                cy="40"
                r={RING_RADIUS}
                fill="none"
                stroke="#4668fd"
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray={RING_CIRC}
                strokeDashoffset={dashOffset}
                style={{ transition: ringTransition }}
              />
            </svg>
            <span style={ringNumberStyle}>{secondsLeft}</span>
          </div>
          <span style={countdownCaptionStyle} aria-live="polite">
            {secondsLeft > 0 ? 'Automatyczne przeniesienie…' : 'Trwa przenoszenie…'}
          </span>
        </div>

        <a href={targetUrl} style={linkStyle} onClick={onContinue}>
          Kliknij tutaj aby kontynuować
        </a>

        <span style={urlStyle}>{MIGRATION_BASE_URL}</span>
      </div>
    </div>
  );
};

export default MigrationModal;



// WEBPACK FOOTER //
// ./src/components/MigrationModal/MigrationModal.Component.tsx